/**
 * Provider credential acquisition — the client half of Google / Apple / Telegram.
 *
 * Each function returns a credential string that the backend verifies in
 * apps/accounts/services/providers.py. Nothing here is trusted: the client only
 * *obtains* a credential, the server decides what it proves.
 *
 * All three load their SDK on demand rather than at boot. A player who taps
 * "Continue as Guest" should never pay for three third-party scripts, and the
 * login screen is the very first thing rendered.
 *
 * Availability is honest: `isAvailable` reports what this build can actually do,
 * so the login screen can hide a button rather than offer one that fails. Apple
 * on Android and Telegram outside a Mini App are the two real cases.
 */

/** How the credential was obtained, for the error messages. */
export type Provider = 'google' | 'apple' | 'telegram';

export class ProviderCancelled extends Error {
  constructor() {
    super('Sign-in was cancelled.');
    this.name = 'ProviderCancelled';
  }
}

export class ProviderUnavailable extends Error {
  constructor(provider: Provider, why: string) {
    super(why);
    this.name = 'ProviderUnavailable';
    this.provider = provider;
  }
  readonly provider: Provider;
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';
const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_CLIENT_ID ?? '';
const APPLE_REDIRECT = import.meta.env.VITE_APPLE_REDIRECT_URI ?? '';

/**
 * True in dev, where the backend trusts any credential string (DEBUG shortcut in
 * services/providers.py). Lets the four login paths be exercised end to end
 * before the real provider apps exist.
 */
export const DEV_CREDENTIALS = import.meta.env.DEV;

/** Load a script once, resolving when it is ready. */
const loaded = new Map<string, Promise<void>>();
function loadScript(src: string): Promise<void> {
  const existing = loaded.get(src);
  if (existing) return existing;

  const p = new Promise<void>((resolve, reject) => {
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => {
      loaded.delete(src);   // allow a retry after a network blip
      reject(new Error('Could not load the sign-in script.'));
    };
    document.head.appendChild(el);
  });
  loaded.set(src, p);
  return p;
}

/* ── Telegram ──────────────────────────────────────────────────────────────
   Inside a Mini App the credential is already present: window.Telegram.WebApp
   .initData is the signed string, no round trip needed. */

interface TelegramWebApp {
  initData?: string;
  ready?: () => void;
  expand?: () => void;
}

function telegramWebApp(): TelegramWebApp | null {
  const tg = (globalThis as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp;
  return tg ?? null;
}

export function inTelegram(): boolean {
  const tg = telegramWebApp();
  return !!tg?.initData;
}

async function getTelegram(): Promise<string> {
  const tg = telegramWebApp();
  if (!tg?.initData) {
    throw new ProviderUnavailable('telegram', 'Open ShweTap inside Telegram to use this.');
  }
  tg.ready?.();
  return tg.initData;
}

/* ── Google ────────────────────────────────────────────────────────────────
   Google Identity Services, ID-token flow. `prompt()` is deliberately not used:
   it is blocked in many embedded webviews, so the button renders a real GIS
   button and we wait for its callback. */

interface GoogleAccounts {
  id: {
    initialize(o: { client_id: string; callback: (r: { credential?: string }) => void }): void;
    renderButton(el: HTMLElement, o: Record<string, unknown>): void;
  };
}

async function getGoogle(mount: HTMLElement): Promise<string> {
  if (!GOOGLE_CLIENT_ID) {
    throw new ProviderUnavailable('google', 'Google sign-in is not configured in this build.');
  }
  await loadScript('https://accounts.google.com/gsi/client');

  const gsi = (globalThis as { google?: { accounts?: GoogleAccounts } }).google?.accounts;
  if (!gsi) throw new ProviderUnavailable('google', 'Google sign-in is unavailable.');

  return new Promise<string>((resolve, reject) => {
    gsi.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (r) => (r.credential ? resolve(r.credential) : reject(new ProviderCancelled())),
    });
    mount.replaceChildren();
    gsi.id.renderButton(mount, {
      theme: 'filled_black', size: 'large', shape: 'pill',
      text: 'continue_with', width: 260,
    });
  });
}

/* ── Apple ─────────────────────────────────────────────────────────────────
   Sign in with Apple JS. On a native iOS shell this is replaced by the
   Capacitor plugin, which returns the same identityToken. */

interface AppleID {
  auth: {
    init(o: Record<string, unknown>): void;
    signIn(): Promise<{ authorization?: { id_token?: string } }>;
  };
}

export function appleSupported(): boolean {
  /* Apple's JS flow works in any browser, but offering it on Android is a dead
     end for most players — they have no Apple ID set up on the device. */
  if (!APPLE_CLIENT_ID) return false;
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod|Macintosh/.test(ua) || !/Android/.test(ua);
}

async function getApple(): Promise<string> {
  if (!APPLE_CLIENT_ID) {
    throw new ProviderUnavailable('apple', 'Apple sign-in is not configured in this build.');
  }
  await loadScript(
    'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
  );
  const appleid = (globalThis as { AppleID?: AppleID }).AppleID;
  if (!appleid) throw new ProviderUnavailable('apple', 'Apple sign-in is unavailable.');

  appleid.auth.init({
    clientId: APPLE_CLIENT_ID,
    scope: 'name email',
    redirectURI: APPLE_REDIRECT || globalThis.location.origin,
    usePopup: true,
  });

  try {
    const res = await appleid.auth.signIn();
    const token = res.authorization?.id_token;
    if (!token) throw new ProviderCancelled();
    return token;
  } catch (e) {
    /* Apple reports a closed popup as an error, which is a cancellation. */
    if (e instanceof ProviderCancelled) throw e;
    throw new ProviderCancelled();
  }
}

/**
 * Is this provider usable right now?
 *
 * In dev everything is available, because the backend accepts any string and the
 * point is to exercise all four paths.
 */
export function isAvailable(provider: Provider): boolean {
  if (DEV_CREDENTIALS) return true;
  switch (provider) {
    case 'telegram': return inTelegram();
    case 'google': return !!GOOGLE_CLIENT_ID;
    case 'apple': return appleSupported();
  }
}

/**
 * Obtain a credential for `provider`.
 *
 * `mount` is where Google's own button is drawn — GIS refuses to run its ID
 * token flow from a synthetic click in most webviews, so it needs a real
 * element. The other two providers ignore it.
 */
export async function credentialFor(provider: Provider, mount?: HTMLElement): Promise<string> {
  if (DEV_CREDENTIALS) {
    /* The backend's DEBUG branch hashes whatever it is given, so a stable string
       per provider means a stable dev account per provider. */
    return `dev-${provider}-credential`;
  }
  switch (provider) {
    case 'telegram': return getTelegram();
    case 'apple': return getApple();
    case 'google': {
      if (!mount) throw new ProviderUnavailable('google', 'Google sign-in could not start.');
      return getGoogle(mount);
    }
  }
}
